import {
  FILTERED,
  FluidFormElementType,
  FluidRowData,
  FluidTableRowData,
  FluidTableSelectionOptions,
  FluidToggleType,
  isNullOrUndefined,
  SEARCHED,
  VISIBLE,
} from '@lmig/fluid-core';
import { forceUpdate } from '@stencil/core';

import { ID, SELECTED } from '../../../constants/constants';
import { DataItem } from '../../../model/typings/fluid-generic.type';
import { Utils } from '../../../utils/utils';
import { FluidTable } from '../fluid-table';
import { setGroupedDataSource } from './filter/fluid-table-filter.utils';
import { is } from './fluid-table-data-management.utils';
import { emitTableUpdates } from './fluid-table-editable-cell-management.utils';

/**
 * Returns the checkbox restriction rules
 * @param disableRowIf
 * @param disabledIf
 * @param selectableIf
 * @param data
 * @returns
 */
export const getCheckBoxRestrictionRules = (
  disableRowIf: (rowData: FluidTableRowData<DataItem>) => boolean,
  disabledIf: (rowData: FluidTableRowData<DataItem>) => boolean,
  selectableIf: (rowData: FluidTableRowData<DataItem>) => boolean,
  data: FluidTableRowData<DataItem>
) => {
  const disabledRow = Utils.isFunction(disableRowIf) && disableRowIf(data);
  const disabled = Utils.isFunction(disabledIf) && disabledIf(data);
  const unselectable = Utils.isFunction(selectableIf) && selectableIf(data);
  return {
    disabledRow,
    disabled,
    unselectable,
  };
};

/**
 * Toggles the selected state of the given dataItem - if a selectableDataPath is
 * configured, then updates the dataItem at that key, and updates the overall
 * datasource.
 * @param host
 * @param dataItem
 * @param mutuallyExclusive
 */
export const selectRow = (
  host: Partial<FluidTable>,
  dataItem: FluidRowData,
  mutuallyExclusive: boolean
) => {
  if (mutuallyExclusive) {
    toggleSelectAll(host, false, true);
  }
  const update = {
    [SELECTED]: !is(dataItem, host._dataSource, SELECTED),
  };
  if (host.selectableDataPath) {
    update[host.selectableDataPath] = !is(
      dataItem,
      host._dataSource,
      host.selectableDataPath
    );
  }
  host.updateDataSource(dataItem[ID], update, true);
  setGroupedDataSource(host);
};

/**
 * Returns a boolean of the dataItem's selected state or, if a selectableDataPath is configured,
 * the value at the dataItems selectableDataPath key.
 * @param host
 * @param dataItem
 */
export const isSelected = (host, dataItem) =>
  host.selectableDataPath
    ? is(dataItem, host._dataSource, host.selectableDataPath) ??
      is(dataItem, host._dataSource, SELECTED)
    : is(dataItem, host._dataSource, SELECTED);

/**
 * Toggles the selected state of all dataItems and emits the table state.
 * @param host
 * @param allSelected
 * @param skipMutationUpdate
 */
export const toggleSelectAll = (
  host: Partial<FluidTable>,
  allSelected: boolean,
  skipMutationUpdate = false
) => {
  const setSelected = (dataItem: FluidTableRowData<DataItem>) => {
    const {
      disabledIf,
      selectableIf,
      selectVisibleOnly,
      selectUnfilteredOnly,
    } = host.selectionOptions ?? {};

    const { disabledRow, disabled, unselectable } = getCheckBoxRestrictionRules(
      host.disableRowIf,
      disabledIf,
      selectableIf,
      dataItem
    );

    const isRowVisible = dataItem[VISIBLE];
    const isFiltered = dataItem[FILTERED] || dataItem[SEARCHED];

    // Skip selection of restricted checkboxes
    const isSelected =
      !(disabledRow || disabled || unselectable) && allSelected;

    const update = { [SELECTED]: isSelected };

    if (selectVisibleOnly) {
      update[SELECTED] = isRowVisible ? isSelected : false;
    } else if (selectUnfilteredOnly) {
      update[SELECTED] = !isFiltered ? isSelected : false;
    }

    // If "selectableDataPath" is configured, update the dataItem at that key
    if (host.selectableDataPath) {
      update[host.selectableDataPath] = update[SELECTED];
    }

    return { ...dataItem, ...update };
  };
  host._dataSource = host._dataSource.map((ds) => setSelected(ds));
  setGroupedDataSource(host);
  forceUpdate(host);
  if (!skipMutationUpdate) {
    emitTableUpdates(host);
  }
};

/**
 * Sets the SELECTED property of all dataItems to false.
 * @param host
 */
export const deselectAll = (host: Partial<FluidTable>) => {
  host._dataSource = host._dataSource.map(
    (dataItem: FluidTableRowData<any>) => ({
      ...dataItem,
      [SELECTED]: false,
    })
  );
};

export const isValidValue = (
  value: any,
  type: FluidFormElementType
): boolean => {
  return type === FluidFormElementType.TOGGLE
    ? value === false || value === true
    : !isNullOrUndefined(value);
};

/**
 * Converts a supposedly boolean value to boolean if it matches any of
 * the defined 'common' values for checked.
 * @param value
 */
const CHECKED = ['Y', 'Yes', 1, 'checked', true, 'true'];
const UNCHECKED = ['N', 'No', 0, 'unchecked', false, 'false', undefined, null];
export const convertNonBooleanIndicatorToBoolean = (value) => {
  if (CHECKED.indexOf(value) !== -1) {
    return true;
  } else if (UNCHECKED.indexOf(value) !== -1) {
    return false;
  }
  return undefined;
};

export const getSelectColumnWidth = (
  selectionOptions: FluidTableSelectionOptions,
  stickyColumn: boolean,
  columnAdjustment: false | string
) => {
  const { selectable, toggleType } = selectionOptions ?? {};

  switch (true) {
    case !selectable:
      return false;
    case !!columnAdjustment:
      return columnAdjustment;
    case stickyColumn:
      return toggleType === FluidToggleType.SWITCH ? '96px' : '48px';
    default:
      return false;
  }
};
